import { Provider } from '@angular/core';
import { select, Store } from '@ngrx/store';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { combineLatest, filter, map, Observable, take } from 'rxjs';

dayjs.extend(localizedFormat);
import {
  APP_TENANT_CONFIG,
  TenantConfig
} from '@bling-fe/shared/tenant-config';
import {
  EDITOR_CONTENT_LINK_SEARCH,
  EditorContentLinkTarget
} from '@bling-fe/shared/ui-components/ui-markdown-editor';
import {
  ContentSearchItem,
  ContentSearchRepository
} from '../repositories/content-search.repository';
import { getCurrentTenant } from '../../core/states/tenant-state/tenant.selectors';
import { Tenant } from '../../core/declarations/tenant.interface';
import { appOrigin } from './app-link.service';

const TYPE_ROUTES: Record<ContentSearchItem['type'], string> = {
  document: 'documents',
  post: 'post',
  event: 'event',
  group: 'group'
};

// Event subtitles arrive as an ISO date (stable); show them in the viewer's
// locale ("16. Juli 2026") instead of raw "2026-07-16".
const formatSubtitle = (subtitle?: string): string | undefined => {
  if (!subtitle) return undefined;
  const date = dayjs(subtitle);
  return date.isValid() ? date.format('LL') : subtitle;
};

/**
 * Backs the editor's inline "#" content-link trigger with the aggregate
 * content-search API. Every result links to its stable app route.
 */
export function provideEditorContentLinkSearch(): Provider {
  return {
    provide: EDITOR_CONTENT_LINK_SEARCH,
    deps: [ContentSearchRepository, Store, APP_TENANT_CONFIG],
    useFactory:
      (
        contentSearchRepository: ContentSearchRepository,
        store: Store,
        tenantConfigs: TenantConfig[]
      ) =>
      (query: string): Observable<EditorContentLinkTarget[]> =>
        combineLatest([
          contentSearchRepository.search(query),
          store.pipe(
            select(getCurrentTenant),
            filter((t): t is Tenant => t !== 'loading' && t !== null),
            take(1)
          )
        ]).pipe(
          map(([response, tenant]) =>
            (response.items ?? []).map(item => ({
              type: item.type,
              title: item.title,
              // only events carry an ISO date; a document's origin label (e.g. a
              // Siedlung named like a date) must render verbatim, not reformatted
              subtitle:
                item.type === 'event'
                  ? formatSubtitle(item.subtitle)
                  : item.subtitle,
              url: `${appOrigin(tenant, tenantConfigs)}/${TYPE_ROUTES[item.type]}/${item.id}`
            }))
          )
        )
  };
}
